import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Campaign, CampaignCreate } from '../types/campaign';
import { campaignService } from '../services/campaignService';
import CampaignList from '../components/CampaignList';
import CampaignForm from '../components/CampaignForm';
import { Box, Typography, Button, CircularProgress, Alert, Paper } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

const CampaignsPage: React.FC = () => {
  const { token } = useAuth();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);

  useEffect(() => {
    const fetchCampaigns = async () => {
      if (!token) {
        setError('Not authenticated - No token available');
        setLoading(false);
        return;
      }

      try {
        console.log("Attempting to fetch campaigns with token", token.substring(0, 10) + "...");
        const data = await campaignService.getCampaigns(token);
        
        // Normalize campaign status to lowercase for frontend consistency
        const normalizedCampaigns = data.map(campaign => ({
          ...campaign,
          status: typeof campaign.status === 'string' ? campaign.status.toLowerCase() as 'active' | 'paused' | 'completed' : campaign.status
        }));
        
        setCampaigns(normalizedCampaigns);
        setError(null);
      } catch (err: any) {
        console.error("Campaign fetch error:", err);
        if (err.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          console.error("Response data:", err.response.data);
          console.error("Response status:", err.response.status);
          console.error("Response headers:", err.response.headers);
          
          if (err.response.status === 401) {
            setError('Authentication error: Your session may have expired. Please log in again.');
            // Optionally force logout here
            // logout();
          } else {
            setError(`Failed to fetch campaigns: ${err.response.data.detail || err.message}`);
          }
        } else if (err.request) {
          // The request was made but no response was received
          console.error("Request was made but no response received:", err.request);
          setError('Network error: Server did not respond. Please try again later.');
        } else {
          // Something happened in setting up the request that triggered an Error
          console.error("Error setting up the request:", err.message);
          setError(`Error: ${err.message}`);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, [token]);

  const handleCreate = async (data: CampaignCreate) => {
    if (!token) {
      setError('Not authenticated');
      return;
    }

    try {
      const newCampaign = await campaignService.createCampaign(token, data);
      
      // Normalize new campaign status to lowercase
      const normalizedCampaign = {
        ...newCampaign,
        status: typeof newCampaign.status === 'string' ? newCampaign.status.toLowerCase() as 'active' | 'paused' | 'completed' : newCampaign.status
      };
      
      setCampaigns([...campaigns, normalizedCampaign]);
      setShowForm(false);
    } catch (err) {
      setError('Failed to create campaign');
    }
  };

  const handleUpdate = async (id: number, data: Partial<Campaign>) => {
    if (!token) {
      setError('Not authenticated');
      return;
    }

    try {
      const updatedCampaign = await campaignService.updateCampaign(token, id, data);
      
      // Normalize updated campaign status to lowercase
      const normalizedCampaign = {
        ...updatedCampaign,
        status: typeof updatedCampaign.status === 'string' ? updatedCampaign.status.toLowerCase() as 'active' | 'paused' | 'completed' : updatedCampaign.status
      };
      
      setCampaigns(campaigns.map(campaign => 
        campaign.id === id ? normalizedCampaign : campaign
      ));
      setEditingCampaign(null);
    } catch (err) {
      setError('Failed to update campaign');
    }
  };

  const handleDelete = async (id: number) => {
    if (!token) {
      setError('Not authenticated');
      return;
    }

    try {
      await campaignService.deleteCampaign(token, id);
      setCampaigns(campaigns.filter(campaign => campaign.id !== id));
    } catch (err) {
      setError('Failed to delete campaign');
    }
  };

  const handlePause = async (id: number) => {
    if (!token) {
      setError('Not authenticated');
      return;
    }

    try {
      await campaignService.pauseCampaign(token, id);
      setCampaigns(campaigns.map(campaign => 
        campaign.id === id ? { ...campaign, status: 'paused' } : campaign
      ));
    } catch (err) {
      setError('Failed to pause campaign');
    }
  };

  const handleResume = async (id: number) => {
    if (!token) {
      setError('Not authenticated');
      return;
    }

    try {
      await campaignService.resumeCampaign(token, id);
      setCampaigns(campaigns.map(campaign => 
        campaign.id === id ? { ...campaign, status: 'active' } : campaign
      ));
    } catch (err) {
      setError('Failed to resume campaign');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, margin: '0 auto', padding: 3 }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4 
      }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Advertising Campaigns
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingCampaign(null);
            setShowForm(true);
          }}
          sx={{
            bgcolor: '#3B82F6',
            '&:hover': { bgcolor: '#2563EB' },
            borderRadius: 2,
            textTransform: 'none',
            px: 3,
            py: 1
          }}
        >
          Create Campaign
        </Button>
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            borderRadius: 2,
            '& .MuiAlert-message': { color: '#EF4444' }
          }}
        >
          {error}
        </Alert>
      )}

      {showForm ? (
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            mb: 4, 
            bgcolor: '#1F2937',
            borderRadius: 2,
            border: '1px solid #374151'
          }}
        >
          <CampaignForm
            onSubmit={editingCampaign ? (data) => handleUpdate(editingCampaign.id, data) : handleCreate}
            onCancel={() => {
              setShowForm(false);
              setEditingCampaign(null);
            }}
            initialData={editingCampaign}
          />
        </Paper>
      ) : campaigns.length === 0 ? (
        <Paper 
          sx={{ 
            p: 4, 
            textAlign: 'center',
            bgcolor: '#1F2937',
            borderRadius: 2,
            border: '1px solid #374151'
          }}
        >
          <Typography variant="h6" sx={{ color: '#9CA3AF', mb: 2 }}>
            No campaigns yet
          </Typography>
          <Typography variant="body1" sx={{ color: '#6B7280', mb: 3 }}>
            Create your first campaign to start advertising
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingCampaign(null);
              setShowForm(true);
            }}
            sx={{
              bgcolor: '#3B82F6',
              '&:hover': { bgcolor: '#2563EB' },
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              py: 1
            }}
          >
            Create Campaign
          </Button>
        </Paper>
      ) : (
        <CampaignList
          campaigns={campaigns}
          onEdit={(campaign) => {
            setEditingCampaign(campaign);
            setShowForm(true);
          }}
          onDelete={handleDelete}
          onPause={handlePause}
          onResume={handleResume}
        />
      )}
    </Box>
  );
};

export default CampaignsPage; 